import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  Check,
  X,
  Upload,
  Loader2,
  MessageCircle,
  UserPlus,
  Link,
  Send,
  ChevronDown,
  AlertCircle,
  FileText,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import CoupleNavigation from './CoupleNavigation';
import { coupleAuth, wedding } from '../../services/api';

const GROUPS = ['Family', 'Friends', 'Work', 'Other'];

const STATUS_BADGES = {
  attending: { label: 'Attending', classes: 'bg-green-100 text-green-700' },
  yes: { label: 'Attending', classes: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', classes: 'bg-red-100 text-red-700' },
  no: { label: 'Declined', classes: 'bg-red-100 text-red-700' },
  maybe: { label: 'Maybe', classes: 'bg-yellow-100 text-yellow-700' },
  pending: { label: 'Pending', classes: 'bg-gray-100 text-gray-500' }
};

function GuestManager() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [couple, setCouple] = useState(null);
  const [page, setPage] = useState(null);
  const [guests, setGuests] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublished, setIsPublished] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', group_name: 'Family',
    plus_one_allowed: false, dietary_requirements: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  // Import state
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // UI state
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [meRes, pageRes, guestsRes, eventsRes] = await Promise.all([
        coupleAuth.getMe(),
        wedding.getMyPage(),
        wedding.getGuests(),
        wedding.getEvents()
      ]);
      setCouple(meRes.data);
      setPage(pageRes.data);
      setIsPublished(pageRes.data?.is_published || false);
      setGuests(guestsRes.data || []);
      setEvents(eventsRes.data || []);
    } catch (err) {
      console.error('Guest manager load error:', err);
      if (err.response?.status === 401) {
        coupleAuth.logout();
        navigate('/couple/login');
        return;
      }
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePublishToggle = async () => {
    try {
      const newState = !isPublished;
      await wedding.publishPage(newState);
      setIsPublished(newState);
    } catch (err) {
      console.error('Publish toggle error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', phone: '', group_name: 'Family',
      plus_one_allowed: false, dietary_requirements: '', notes: ''
    });
    setEditingGuest(null);
    setShowForm(false);
  };

  const openEditForm = (guest) => {
    setFormData({
      name: guest.name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      group_name: guest.group_name || 'Family',
      plus_one_allowed: guest.plus_one_allowed || false,
      dietary_requirements: guest.dietary_requirements || '',
      notes: guest.notes || ''
    });
    setEditingGuest(guest);
    setShowForm(true);
  };

  const handleSaveGuest = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    try {
      setSaving(true);
      if (editingGuest) {
        await wedding.updateGuest(editingGuest.id, formData);
      } else {
        await wedding.addGuest(formData);
      }
      resetForm();
      await loadData();
    } catch (err) {
      console.error('Save guest error:', err);
      setError(err.response?.data?.error || 'Failed to save guest.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGuest = async (id) => {
    try {
      setDeletingId(id);
      await wedding.deleteGuest(id);
      setGuests((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error('Delete guest error:', err);
      setError('Failed to delete guest.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleWhatsApp = async (guestId) => {
    try {
      const res = await wedding.getWhatsAppLink(guestId);
      const url = res.data?.url || res.data?.whatsapp_url;
      if (url) window.open(url, '_blank');
    } catch (err) {
      console.error('WhatsApp link error:', err);
      setError('Failed to generate WhatsApp link.');
    }
  };

  const handleCopyRsvpLink = (guest) => {
    const link = guest.rsvp_token
      ? `${window.location.origin}/rsvp/${guest.rsvp_token}`
      : null;
    if (!link) {
      setError('No RSVP token found for this guest.');
      return;
    }
    navigator.clipboard.writeText(link);
    setCopiedId(guest.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // --- Import logic ---

  // Parse rows from an array of objects (from Excel or CSV)
  const parseRows = (rows) => {
    return rows.map((row) => {
      // Normalise keys to lowercase
      const r = {};
      Object.keys(row).forEach(k => { r[k.toLowerCase().trim()] = row[k]; });

      const plusVal = String(r['plus one'] || r['plus_one'] || r['plusone'] || '').toLowerCase();
      return {
        name: String(r.name || '').trim(),
        email: String(r.email || '').trim(),
        phone: String(r.phone || r.telephone || r.cell || r.mobile || '').trim(),
        group_name: String(r.group || r.group_name || r.category || 'Family').trim(),
        plus_one_allowed: plusVal === 'yes' || plusVal === 'true' || plusVal === '1'
      };
    }).filter((g) => g.name);
  };

  const parseImportData = (text) => {
    const lines = text.trim().split('\n').filter((l) => l.trim());
    if (lines.length === 0) return [];

    // Try JSON first
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parseRows(parsed);
    } catch {
      // Not JSON, try CSV
    }

    // CSV: header line then data rows -> convert to objects
    const header = lines[0].split(',').map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(',').map((c) => c.trim());
      const obj = {};
      header.forEach((h, i) => { obj[h] = cols[i] || ''; });
      return obj;
    });
    return parseRows(rows);
  };

  const handlePreviewImport = () => {
    const parsed = parseImportData(importText);
    setImportPreview(parsed);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xlsx' || ext === 'xls') {
      // Excel file
      const reader = new FileReader();
      reader.onload = (ev) => {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const parsed = parseRows(rows);
        setImportPreview(parsed);
        setImportText(`${parsed.length} guests loaded from ${file.name}`);
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV / text
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImportText(ev.target.result);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      { Name: 'John Smith', Email: 'john@email.com', Phone: '0821234567', Group: 'Family', 'Plus One': 'Yes' },
      { Name: 'Jane Doe', Email: 'jane@email.com', Phone: '0839876543', Group: 'Friends', 'Plus One': 'No' },
      { Name: 'David & Mary Jones', Email: 'david@email.com', Phone: '0711112222', Group: 'Family', 'Plus One': 'No' },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    // Set column widths
    ws['!cols'] = [{ wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 10 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Guest List');
    XLSX.writeFile(wb, 'guest_list_template.xlsx');
  };

  const handleImportSubmit = async () => {
    if (importPreview.length === 0) return;
    try {
      setImporting(true);
      await wedding.importGuests(importPreview);
      setShowImport(false);
      setImportText('');
      setImportPreview([]);
      await loadData();
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.error || 'Failed to import guests.');
    } finally {
      setImporting(false);
    }
  };

  const handleSendAllInvites = async () => {
    const unsent = guests.filter((g) => !g.invite_sent && g.phone);
    for (const guest of unsent) {
      try {
        const res = await wedding.getWhatsAppLink(guest.id);
        const url = res.data?.url || res.data?.whatsapp_url;
        if (url) window.open(url, '_blank');
      } catch (err) {
        console.error(`WhatsApp error for ${guest.name}:`, err);
      }
    }
  };

  // Filtering
  const filteredGuests = guests.filter((g) => {
    const matchesSearch = !searchTerm ||
      g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = !filterGroup || g.group_name === filterGroup;
    const status = g.rsvp_status || 'pending';
    const matchesStatus = !filterStatus || status === filterStatus;
    return matchesSearch && matchesGroup && matchesStatus;
  });

  // Stats
  const totalGuests = guests.length;
  const attending = guests.filter((g) => g.rsvp_status === 'attending' || g.rsvp_status === 'yes').length;
  const declined = guests.filter((g) => g.rsvp_status === 'declined' || g.rsvp_status === 'no').length;
  const maybe = guests.filter((g) => g.rsvp_status === 'maybe').length;
  const pending = totalGuests - attending - declined - maybe;
  const invited = guests.filter((g) => g.invite_sent).length;

  const stats = [
    { label: 'Total', value: totalGuests, color: 'text-gray-900' },
    { label: 'Invited', value: invited, color: 'text-blue-600' },
    { label: 'Attending', value: attending, color: 'text-green-600' },
    { label: 'Declined', value: declined, color: 'text-red-600' },
    { label: 'Pending', value: pending, color: 'text-gray-500' }
  ];

  if (loading) {
    return (
      <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
        </div>
      </CoupleNavigation>
    );
  }

  return (
    <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Guest List</h1>
            <p className="text-gray-500 text-sm mt-1">Manage your wedding guests and invitations</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Guest
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats bar */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
              <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filters + Send All */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search guests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">All Groups</option>
            {GROUPS.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="attending">Attending</option>
            <option value="declined">Declined</option>
            <option value="maybe">Maybe</option>
            <option value="pending">Pending</option>
          </select>
          {guests.some((g) => !g.invite_sent && g.phone) && (
            <button
              onClick={handleSendAllInvites}
              className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <Send className="w-4 h-4" />
              Send All Invites
            </button>
          )}
        </div>

        {/* Guest table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Phone</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">Group</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Plus One</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">RSVP</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      {guests.length === 0
                        ? 'No guests yet. Add your first guest above.'
                        : 'No guests match your filters.'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredGuests.map((guest) => {
                    const status = guest.rsvp_status || 'pending';
                    const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
                    return (
                      <tr key={guest.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{guest.name}</td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{guest.email || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{guest.phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{guest.group_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                          {guest.plus_one_allowed ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <UserPlus className="w-3.5 h-3.5" /> Yes
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditForm(guest)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {guest.phone && (
                              <button
                                onClick={() => handleWhatsApp(guest.id)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="WhatsApp"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleCopyRsvpLink(guest)}
                              className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Copy RSVP Link"
                            >
                              {copiedId === guest.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Link className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete ${guest.name}?`)) {
                                  handleDeleteGuest(guest.id);
                                }
                              }}
                              disabled={deletingId === guest.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              {deletingId === guest.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Guest Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingGuest ? 'Edit Guest' : 'Add Guest'}
                </h2>
                <button onClick={resetForm} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSaveGuest} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                  <select
                    value={formData.group_name}
                    onChange={(e) => setFormData((p) => ({ ...p, group_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {GROUPS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.plus_one_allowed}
                    onChange={(e) => setFormData((p) => ({ ...p, plus_one_allowed: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-500"
                  />
                  <span className="text-sm text-gray-700">Allow plus one</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Requirements</label>
                  <input
                    type="text"
                    value={formData.dietary_requirements}
                    onChange={(e) => setFormData((p) => ({ ...p, dietary_requirements: e.target.value }))}
                    placeholder="e.g. Vegetarian, nut allergy"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {editingGuest ? 'Update' : 'Add Guest'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImport && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Import Guests</h2>
                <button
                  onClick={() => { setShowImport(false); setImportText(''); setImportPreview([]); }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Download the Excel template, fill in your guest list, then upload it.
                </p>

                {/* Step 1: Download template */}
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-4">
                  <p className="text-sm font-medium text-rose-800 mb-2">Step 1: Download the template</p>
                  <button
                    onClick={downloadTemplate}
                    className="px-4 py-2.5 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors flex items-center gap-2 shadow-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel Template
                  </button>
                  <p className="text-xs text-rose-600 mt-2">Open in Excel or Google Sheets, fill in your guests, then save.</p>
                </div>

                {/* Step 2: Upload */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-gray-800 mb-2">Step 2: Upload your guest list</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2.5 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:border-rose-300 hover:bg-rose-50 transition-colors flex items-center gap-2 w-full justify-center"
                  >
                    <Upload className="w-4 h-4" />
                    Choose Excel or CSV File
                  </button>
                  {importText && importPreview.length === 0 && (
                    <button
                      onClick={handlePreviewImport}
                      className="mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors w-full"
                    >
                      Preview Import
                    </button>
                  )}
                </div>

                {importPreview.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Preview ({importPreview.length} guests)
                    </p>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-3 py-1.5">Name</th>
                            <th className="text-left px-3 py-1.5">Email</th>
                            <th className="text-left px-3 py-1.5">Phone</th>
                            <th className="text-left px-3 py-1.5">Group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((g, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="px-3 py-1.5">{g.name}</td>
                              <td className="px-3 py-1.5">{g.email || '-'}</td>
                              <td className="px-3 py-1.5">{g.phone || '-'}</td>
                              <td className="px-3 py-1.5">{g.group_name || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      onClick={handleImportSubmit}
                      disabled={importing}
                      className="mt-3 w-full px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Import {importPreview.length} Guests
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </CoupleNavigation>
  );
}

export default GuestManager;
